import { Box, Icon, Typography } from "@outschool/backpack";
import { faFileVideo, faImage, faPaperclip } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import React from "react";

import { ThreadItem } from "./types";

function getMediaPreview(thread: ThreadItem) {
  const content = thread.latestMessage?.messageContent;
  if (!content) {
    return null;
  }
  if (content.video) {
    return { icon: faFileVideo, type: "video" } as const;
  }
  const attachments = content.attachments ?? [];
  if (attachments.length === 0) {
    return null;
  }
  const hasImage = attachments.some(a =>
    a.file?.mimetype?.startsWith("image/"),
  );
  if (hasImage) {
    return { icon: faImage, type: "photo" } as const;
  }
  return { icon: faPaperclip, type: "attachment" } as const;
}

interface ThreadListItemProps {
  thread: ThreadItem;
  onClick: () => void;
}

export default function ThreadListItem({
  thread,
  onClick,
}: ThreadListItemProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListItem",
  );
  const messagePreview = thread.latestMessage?.messageContent?.text;
  const mediaPreview = !messagePreview ? getMediaPreview(thread) : null;
  const title =
    thread.classPost?.title ?? thread.learningAssignmentClassPost?.title ?? "";
  const timestamp = thread.latestMessage?.sentAt
    ? dayjs(thread.latestMessage.sentAt).fromNow()
    : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        padding: "1.2rem 1.6rem",
        borderBottom: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "background-color 0.15s",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: "0.4rem",
        }}
      >
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            flex: 1,
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        {timestamp && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {timestamp}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {messagePreview != null && messagePreview.length > 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{
              flex: 1,
              fontWeight: thread.unreadCount > 0 ? 600 : 400,
            }}
          >
            {messagePreview}
          </Typography>
        ) : mediaPreview ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            <Icon
              icon={mediaPreview.icon}
              sx={{ fontSize: "1.2rem", color: "text.secondary" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: "italic",
                fontWeight: thread.unreadCount > 0 ? 600 : 400,
              }}
            >
              {mediaPreview.type === "video"
                ? t("Video")
                : mediaPreview.type === "photo"
                ? t("Photo")
                : t("Attachment")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        {thread.unreadCount > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "100%",
              fontSize: "1.4rem",
              fontWeight: 500,
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {thread.unreadCount}
          </Box>
        )}
      </Box>
    </Box>
  );
}
